import axios from "axios";
import keycloakInstance from "@/initKeycloak";
import { attachRetryOn401 } from "@/shared/api/interceptors/retryOn401";
import type { CategoryHistory } from "./types";

// The calendar service that stores per-category history for the mini-charts.
// This is the same service the content-hub calendar feed originates from, but
// the history endpoint isn't exposed through content-hub, so it's fetched here
// directly. Overridable via env; defaults to the public host.
const CALENDAR_API_BASE_URL: string =
  import.meta.env.VITE_CALENDAR_V2_BASE_URL ??
  "https://economic-calendar.newsquawk.com";

// Authenticated client for the calendar service: attaches the Keycloak bearer
// (refreshed if within 30s of expiry) and retries once on a 401, mirroring the
// content-hub client.
const calendarClient = axios.create();

calendarClient.interceptors.request.use(async (config) => {
  if (keycloakInstance && keycloakInstance.authenticated) {
    try {
      await keycloakInstance.updateToken(30);
    } catch {
      // Best-effort refresh — the 401-retry interceptor covers a lapse.
    }
    config.headers.Authorization = `Bearer ${keycloakInstance.token}`;
  }
  return config;
});

attachRetryOn401(calendarClient);

/**
 * Fetch historical released figures for the given data categories since
 * `startDate`. Returns a map keyed by category id, each value the category's
 * ascending occurs_at/actual series (drives the row mini-chart).
 */
export async function fetchCategoryHistory(
  categoryIds: number[],
  startDate: string
): Promise<Record<string, CategoryHistory>> {
  if (categoryIds.length === 0) return {};
  const params = new URLSearchParams();
  for (const id of categoryIds) params.append("category_id", String(id));
  params.set("start_date", startDate);
  const url = `${CALENDAR_API_BASE_URL.replace(/\/+$/, "")}/api/calendar/entries/data?${params.toString()}`;
  const response = await calendarClient.get<Record<string, CategoryHistory>>(url);
  return response.data ?? {};
}
